import { createClient } from 'next-sanity'
import imageUrlBuilder from '@sanity/image-url'
import { SanityImageSource } from '@sanity/image-url/lib/types/types'

// Sanity configuration
export const projectId = process.env.NEXT_PUBLIC_SANITY_PROJECT_ID
export const dataset = process.env.NEXT_PUBLIC_SANITY_DATASET || 'production'
export const apiVersion = process.env.NEXT_PUBLIC_SANITY_API_VERSION || '2023-05-03'

// Determine if we should use local Sanity Studio
const useLocalSanity = process.env.NEXT_PUBLIC_USE_LOCAL_SANITY === 'true';
const studioUrl = useLocalSanity ? 'http://localhost:3333' : `https://${projectId}.sanity.studio`;

// Set up the Sanity client
export const client = createClient({
    projectId,
    dataset,
    apiVersion,
    useCdn: process.env.NODE_ENV === 'production',
    studioUrl,
    // Add development-specific options
    ...(process.env.NODE_ENV === 'development' && {
        token: process.env.SANITY_API_TOKEN, // Only include token in development
    })
})

// Set up the image URL builder
const builder = imageUrlBuilder(client)

/**
 * Helper function to generate image URLs from Sanity image references
 */
export function urlFor(source: SanityImageSource) {
    return builder.image(source)
}

/**
 * Helper function for GROQ queries with type safety
 */
export async function sanityFetch<QueryResponse>({
    query,
    params = {},
    tags = [],
}: {
    query: string
    params?: Record<string, any>
    tags?: string[]
}): Promise<QueryResponse> {
    try {
        return await client.fetch<QueryResponse>(query, params, {
            cache: 'force-cache',
            next: { tags },
        })
    } catch (error: any) {
        console.error('Sanity fetch error:', error);
        // Add more context to the error
        const errorMessage = error?.message || 'Unknown error';
        const enhancedError = new Error(`Request error while attempting to fetch data: ${errorMessage}`);
        // Preserve the original stack trace if possible
        if (error?.stack) {
            enhancedError.stack = error.stack;
        }
        throw enhancedError;
    }
}

/**
 * Helper function for preview mode
 */
export const getClient = (preview?: boolean) => {
    const client = createClient({
        projectId,
        dataset,
        apiVersion,
        useCdn: !preview,
        perspective: preview ? 'previewDrafts' : 'published',
    })

    return client
}